import React, { memo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Flag, Crown, ThumbsUp, AlertTriangle, AlertCircle, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { contactsAPI } from '../../api';

const CustomerStatusDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  currentStatus,
  onSuccess 
}) => {
  const [statusNote, setStatusNote] = useState('');

  const handleSetStatus = useCallback(async (newStatus, note = '') => {
    try {
      await contactsAPI.update(contactId, { 
        customer_status: newStatus,
        status_note: note 
      });
      onOpenChange(false);
      setStatusNote('');
      toast.success(newStatus ? `Customer marked as ${newStatus.replace('_', ' ')}` : 'Status removed');
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to update customer status');
    }
  }, [contactId, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    setStatusNote('');
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5" />
            Set Customer Status
          </DialogTitle>
          <DialogDescription>
            Flag this customer with a status to alert your team
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-3">
            <Button 
              variant={currentStatus === 'vip' ? 'default' : 'outline'}
              className="flex flex-col items-center gap-2 h-20 bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"
              onClick={() => handleSetStatus('vip', statusNote)}
              data-testid="status-vip-btn"
            >
              <Crown className="h-6 w-6" />
              <span>VIP</span>
            </Button>
            <Button 
              variant={currentStatus === 'good' ? 'default' : 'outline'}
              className="flex flex-col items-center gap-2 h-20 bg-green-50 border-green-200 text-green-800 hover:bg-green-100"
              onClick={() => handleSetStatus('good', statusNote)}
              data-testid="status-good-btn"
            >
              <ThumbsUp className="h-6 w-6" />
              <span>Good</span>
            </Button>
            <Button 
              variant={currentStatus === 'hostile' ? 'default' : 'outline'}
              className="flex flex-col items-center gap-2 h-20 bg-red-50 border-red-200 text-red-800 hover:bg-red-100"
              onClick={() => handleSetStatus('hostile', statusNote)}
              data-testid="status-hostile-btn"
            >
              <AlertTriangle className="h-6 w-6" />
              <span>Hostile</span>
            </Button>
            <Button 
              variant={currentStatus === 'escalation' ? 'default' : 'outline'}
              className="flex flex-col items-center gap-2 h-20 bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100"
              onClick={() => handleSetStatus('escalation', statusNote)}
              data-testid="status-escalation-btn"
            >
              <Flag className="h-6 w-6" />
              <span>Escalation</span>
            </Button>
            <Button 
              variant={currentStatus === 'at_risk' ? 'default' : 'outline'}
              className="flex flex-col items-center gap-2 h-20 bg-yellow-50 border-yellow-200 text-yellow-800 hover:bg-yellow-100"
              onClick={() => handleSetStatus('at_risk', statusNote)}
              data-testid="status-at-risk-btn"
            >
              <AlertCircle className="h-6 w-6" />
              <span>At Risk</span>
            </Button>
            <Button 
              variant={currentStatus === 'new' ? 'default' : 'outline'}
              className="flex flex-col items-center gap-2 h-20 bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100"
              onClick={() => handleSetStatus('new', statusNote)}
              data-testid="status-new-btn"
            >
              <Star className="h-6 w-6" />
              <span>New</span>
            </Button>
          </div>
          <div className="space-y-2">
            <Label>Note (optional)</Label>
            <Textarea 
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              placeholder="Add a note about this status..."
              rows={2}
              data-testid="status-note-input"
            />
          </div>
          {currentStatus && (
            <Button 
              variant="ghost" 
              className="w-full text-muted-foreground"
              onClick={() => handleSetStatus(null, '')}
              data-testid="remove-status-btn"
            >
              <X className="h-4 w-4 mr-2" />
              Remove Status
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});

CustomerStatusDialog.displayName = 'CustomerStatusDialog';

export default CustomerStatusDialog;
